import React from 'react';
import styled from 'styled-components';

// Props interface
interface CartSidebarProps {
  onOpenContactModal: () => void;
  t: (key: string) => string;
}

// Styled Components
const RightSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: sticky;
  top: 5.5rem;
  align-self: flex-start;
  z-index: 10;
  max-height: calc(100vh - 6.5rem);
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    max-height: none;
    overflow-y: visible;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ServiceSection = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 24px;
  box-shadow: 0 10px 28px rgba(16, 24, 40, 0.06);
  border: 1px solid #e6edf5;

  .dark & {
    background: #0f1216;
    border: 1px solid #2e3441;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6);
  }

  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 20px;
  }

  @media (max-width: 480px) {
    border-radius: 16px;
    padding: 16px;
  }

  @media (max-width: 360px) {
    border-radius: 14px;
    padding: 14px;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 20px 0;
  color: #111827;

  .dark & {
    color: #e5e7eb;
  }

  @media (max-width: 768px) {
    font-size: 19px;
    margin-bottom: 16px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 14px;
  }

  @media (max-width: 360px) {
    font-size: 17px;
    margin-bottom: 12px;
  }
`;

const ServiceFeature = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 480px) {
    gap: 12px;
    margin-bottom: 16px;
  }

  @media (max-width: 360px) {
    gap: 10px;
    margin-bottom: 14px;
  }
`;

const ServiceIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #475569;
  background: linear-gradient(180deg, #f6f8fb 0%, #eef2f7 100%);
  border: 1px solid #e5eaf2;
  border-radius: 12px;

  .dark & {
    color: #94a3b8;
    background: linear-gradient(180deg, #161a21 0%, #0f1216 100%);
    border: 1px solid #2e3441;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }

  @media (max-width: 360px) {
    width: 30px;
    height: 30px;
  }
`;

const FeatureTitle = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #111827;
  letter-spacing: -0.01em;
  margin-bottom: 4px;

  .dark & {
    color: #e5e7eb;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 3px;
  }

  @media (max-width: 360px) {
    font-size: 0.92rem;
  }
`;

const ServiceText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #333;
  line-height: 1.5;

  .dark & {
    color: #cbd5e1;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.45;
  }

  @media (max-width: 360px) {
    font-size: 0.92rem;
  }
`;

const ServiceDivider = styled.hr`
  border: none;
  border-top: 1px dashed #e6eaf0;
  margin: 8px 0;

  .dark & {
    border-top-color: #2e3441;
  }

  @media (max-width: 480px) {
    margin: 6px 0;
  }
`;

const StartChatButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #374151;
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);

  &:hover {
    background: #4b5563;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.35);
  }

  .dark & {
    background: rgba(255, 255, 255, 0.06);
    color: #e5e7eb;
    border: 1px solid #2e3441;
    box-shadow: none;
  }
  .dark &:hover {
    background: rgba(148, 163, 184, 0.08);
    border-color: #3a4250;
  }
`;

// Icons
const ChatButtonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PickupIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

const ShieldCheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 17l-5 3 1-5-4-4 5-.8L12 5l2 5.2 5 .8-4 4 1 5z" />
  </svg>
);

const MessageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// Service features data
const getServiceFeatures = (t: (key: string) => string) => [
  {
    icon: <PickupIcon />,
    title: t('shippingHassleFreTitle'),
    text: t('shippingHassleFreText')
  },
  {
    icon: <ShieldCheckIcon />,
    title: t('dataSecureTitle'),
    text: t('dataSecureText')
  },
  {
    icon: <StarIcon />,
    title: t('trustedTitle'),
    text: t('trustedText')
  }
];

// Main Component
const CartSidebar: React.FC<CartSidebarProps> = ({ onOpenContactModal, t }) => {
  const features = getServiceFeatures(t);

  return (
    <RightSidebar>
      <ServiceSection>
        <ServiceTitle>{t('whyChoosePhoneBros')}</ServiceTitle>
        <div style={{ marginTop: '8px' }}>
          {features.map((item, idx) => (
            <React.Fragment key={idx}>
              <ServiceFeature>
                <ServiceIcon>{item.icon}</ServiceIcon>
                <div style={{ flex: 1 }}>
                  <FeatureTitle>{item.title}</FeatureTitle>
                  <ServiceText>{item.text}</ServiceText>
                </div>
              </ServiceFeature>
              {idx < features.length - 1 && <ServiceDivider />}
            </React.Fragment>
          ))}
          <ServiceDivider />
          <ServiceFeature>
            <ServiceIcon>
              <MessageIcon />
            </ServiceIcon>
            <div style={{ flex: 1 }}>
              <FeatureTitle>{t('humanContactTitle')}</FeatureTitle>
              <ServiceText style={{ marginBottom: '10px' }}>{t('humanContactText')}</ServiceText>
              <StartChatButton onClick={onOpenContactModal}>
                <ChatButtonIcon />
                <span>{t('startChat')}</span>
              </StartChatButton>
            </div>
          </ServiceFeature>
        </div>
      </ServiceSection>
    </RightSidebar>
  );
};

export default CartSidebar;
